import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Slider } from 'primereact/slider';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { SelectButton } from 'primereact/selectbutton';
import { podesavanjaApi } from '../../api/services';
import { useAlerts } from '../../custom/Alerts';
import { SRPSKI } from '../common/RadioPlayer';
import { NAZIVI_EFEKATA, pustiEfekat } from './zvuci';
import { SlikaOkvir } from './Zabava';

const BOJE = [
    { label: 'Zelena', value: 'accent' }, { label: 'Crvena', value: 'danger' }, { label: 'Narandžasta', value: 'warn' },
    { label: 'Plava', value: 'info' }, { label: 'Svetlozelena', value: 'ok' },
];
const ANIMACIJE = [
    { label: 'Konfete', value: 'konfete' }, { label: 'Sneg', value: 'sneg' }, { label: 'Emodži', value: 'emodzi' }, { label: 'Mehurići', value: 'mehurici' },
];
const TEME = [
    { value: 'normalna', label: 'Normalna', boje: ['#163a2e', '#2e8b57', '#f3f6f5'] },
    { value: 'duga', label: 'Duga 🌈', boje: ['#e53935', '#fdd835', '#43a047', '#1e88e5', '#8e24aa'] },
    { value: 'neon', label: 'Neon', boje: ['#0b0420', '#ff2bd6', '#00e5ff'] },
    { value: 'roze', label: 'Roze', boje: ['#ff4fa3', '#ffd1e6', '#fff4f9'] },
    { value: 'matrix', label: 'Matrix', boje: ['#000000', '#00ff41', '#0a3d14'] },
    { value: 'retro', label: 'Retro (sepija)', boje: ['#704214', '#c8a27a', '#f4ecd8'] },
];
const OBLICI = [{ label: 'Original', value: 'original' }, { label: 'Krug', value: 'krug' }, { label: 'Srce ❤️', value: 'srce' }];
const VRSTE_ZVUKA = [{ label: 'Zvučni efekat', value: 'efekat' }, { label: 'Radio', value: 'radio' }];
const MAX_SLIKA = 450 * 1024;
const MAX_ANIMACIJA = 10 * 1024 * 1024; // GIF / WEBP / SVG fajl — čuva se ceo da animacija ostane (server: posebno, po verziji)

/** slika iz fajla -> data URL; GIF/WEBP/SVG ostaju kakvi jesu (platno bi zadržalo samo prvi kadar animacije),
    ostale velike slike se umanjuju (do 320 px, PNG) */
const ucitajSliku = (file) => new Promise((res, rej) => {
    const animirana = /^image\/(gif|webp|svg)/.test(file.type);
    if (animirana && file.size > MAX_ANIMACIJA) { rej(new Error('GIF je prevelik (najviše 10 MB).')); return; }
    const r = new FileReader();
    r.onerror = rej;
    r.onload = () => {
        const url = r.result;
        if (animirana || url.length <= MAX_SLIKA) { res(url); return; }
        const img = new Image();
        img.onload = () => {
            const k = Math.min(1, 320 / Math.max(img.width, img.height));
            const c = document.createElement('canvas');
            c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
            c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
            res(c.toDataURL('image/png'));
        };
        img.onerror = rej;
        img.src = url;
    };
    r.readAsDataURL(file);
});

/** odeljak sa prekidačem */
function Odeljak({ ikona, naslov, opis, ukljuceno, onUkljuceno, children }) {
    return (
        <section className={`zab-odeljak${ukljuceno ? ' ukljuceno' : ''}`}>
            <div className="zab-odeljak-head">
                <span className="zab-ikona" aria-hidden="true"><i className={ikona} /></span>
                <span className="zab-naslov"><b>{naslov}</b><small>{opis}</small></span>
                <InputSwitch checked={ukljuceno} onChange={(e) => onUkljuceno(e.value)} aria-label={naslov} />
            </div>
            {ukljuceno && <div className="zab-odeljak-telo">{children}</div>}
        </section>
    );
}

/* Admin: zupčanik u zaglavlju -> "Zabava za operatere". Promene se šalju UŽIVO izabranim korisnicima (Server-Sent Events). */
export default function AdminPodesavanja() {
    const alerts = useAlerts();
    const [otvoren, setOtvoren] = useState(false);
    const [cfg, setCfg] = useState(null);
    const [korisnici, setKorisnici] = useState([]);
    const [povezano, setPovezano] = useState(0);
    const [cuva, setCuva] = useState(false);

    useEffect(() => {
        if (!otvoren) return;
        podesavanjaApi.zabava().then((c) => { setPovezano(c.povezano || 0); setCfg(c); }).catch(() => {});
        podesavanjaApi.korisnici().then((l) => setKorisnici(Array.isArray(l) ? l : [])).catch(() => {});
    }, [otvoren]);

    const set = (grana, patch) => setCfg((c) => ({ ...c, [grana]: { ...(c[grana] || {}), ...patch } }));
    const sacuvaj = async (izmena) => {
        const novo = izmena ? { ...cfg, ...izmena } : cfg;
        setCuva(true);
        try {
            const r = await podesavanjaApi.sacuvajZabavu(novo);
            setCfg(r); setPovezano(r.povezano || 0);
            alerts.success(r.ukljuceno ? `Primenjeno uživo — povezanih korisnika: ${r.povezano || 0}` : 'Zabava je isključena kod svih');
        } catch { /* toast */ }
        setCuva(false);
    };

    const korisnikOpcije = korisnici.map((k) => ({ label: `${k.firstName || ''} ${k.lastName || ''}`.trim() + (k.email ? ` — ${k.email}` : ''), value: k.id }));

    const footer = cfg && (
        <div className="zab-footer">
            <span className="zab-uzivo"><i className="zab-tacka" aria-hidden="true" /> uživo · povezano {povezano}</span>
            <Button label="Zatvori" text severity="secondary" onClick={() => setOtvoren(false)} />
            <Button label="Primeni odmah" icon="pi pi-bolt" loading={cuva} onClick={() => sacuvaj()} />
        </div>
    );

    return (
        <>
            <button type="button" className="zab-zupcanik" onClick={() => setOtvoren(true)} title="Podešavanja (admin)" aria-label="Podešavanja (admin)">
                <i className="bi bi-gear-fill" aria-hidden="true" />
            </button>
            <Dialog header="Podešavanja — zabava za operatere" visible={otvoren} onHide={() => setOtvoren(false)} footer={footer}
                style={{ width: 'min(46rem, 96vw)' }} modal draggable={false} className="zab-dijalog">
                {!cfg ? <p className="muted"><i className="pi pi-spin pi-spinner" /> Učitavanje…</p> : (
                    <div className="zab-telo">
                        <div className={`zab-glavni${cfg.ukljuceno ? ' ukljuceno' : ''}`}>
                            <div>
                                <b>Zabava je {cfg.ukljuceno ? 'UKLJUČENA' : 'isključena'}</b>
                                <small>Glavni prekidač — promena se odmah vidi kod izabranih korisnika.</small>
                            </div>
                            <InputSwitch checked={!!cfg.ukljuceno} onChange={(e) => { setCfg((c) => ({ ...c, ukljuceno: e.value })); sacuvaj({ ukljuceno: e.value }); }}
                                aria-label="Zabava uključena" />
                        </div>

                        <section className="zab-odeljak ukljuceno">
                            <div className="zab-odeljak-head">
                                <span className="zab-ikona" aria-hidden="true"><i className="pi pi-users" /></span>
                                <span className="zab-naslov"><b>Kome se prikazuje</b><small>Svim operaterima ili samo izabranim korisnicima.</small></span>
                            </div>
                            <div className="zab-odeljak-telo">
                                <SelectButton value={cfg.ciljevi.svi ? 'svi' : 'izabrani'} options={[{ label: 'Svim operaterima', value: 'svi' }, { label: 'Samo izabranim', value: 'izabrani' }]}
                                    onChange={(e) => e.value && set('ciljevi', { svi: e.value === 'svi' })} />
                                {!cfg.ciljevi.svi && (
                                    <MultiSelect value={cfg.ciljevi.korisnici} options={korisnikOpcije} onChange={(e) => set('ciljevi', { korisnici: e.value })}
                                        placeholder="Izaberite korisnike" filter display="chip" className="zab-puno" emptyMessage="Nema korisnika" />
                                )}
                                <div className="zab-red"><InputSwitch inputId="zab-sw-1" checked={!!cfg.ciljevi.iAdmin} onChange={(e) => set('ciljevi', { iAdmin: e.value })} /> <label htmlFor="zab-sw-1">Prikaži i meni (pregled)</label></div>
                            </div>
                        </section>

                        <Odeljak ikona="pi pi-megaphone" naslov="Traka sa tekstom" opis="Tekst prolazi kroz zaglavlje sleva nadesno."
                            ukljuceno={!!cfg.traka.ukljuceno} onUkljuceno={(v) => set('traka', { ukljuceno: v })}>
                            <InputText value={cfg.traka.tekst} onChange={(e) => set('traka', { tekst: e.target.value })} maxLength={300} className="zab-puno" placeholder="Tekst trake" />
                            <div className="zab-red"><span>Brzina prolaza</span><Slider value={61 - cfg.traka.brzina} min={1} max={57} onChange={(e) => set('traka', { brzina: 61 - e.value })} className="zab-slider" /><span>{cfg.traka.brzina}s</span></div>
                            <div className="zab-red"><span>Boja</span><Dropdown value={cfg.traka.boja} options={BOJE} onChange={(e) => set('traka', { boja: e.value })} /></div>
                        </Odeljak>

                        <Odeljak ikona="pi pi-image" naslov="Slika po ekranu" opis="Leti, odbija se od ivica i rotira — ne može se zatvoriti."
                            ukljuceno={!!cfg.slika.ukljuceno} onUkljuceno={(v) => set('slika', { ukljuceno: v })}>
                            <div className="zab-red">
                                <span className="zab-pregled-slike"><SlikaOkvir slika={cfg.slika} /></span>
                                <label className="p-button p-button-outlined p-button-sm zab-fajl">
                                    <i className="pi pi-upload" /> Izaberi sliku ili GIF
                                    <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml" hidden onChange={async (e) => {
                                        const f = e.target.files && e.target.files[0]; e.target.value = '';
                                        if (!f) return;
                                        try { set('slika', { izvor: 'data', data: await ucitajSliku(f) }); } catch (er) { alerts.error((er && er.message) || 'Slika nije mogla da se učita.'); }
                                    }} />
                                </label>
                                {cfg.slika.izvor === 'data' && <Button label="Logo" text size="small" onClick={() => set('slika', { izvor: 'logo', data: null })} />}
                            </div>
                            <div className="zab-red"><span>Okvir</span><SelectButton value={cfg.slika.oblik || 'original'} options={OBLICI} onChange={(e) => e.value && set('slika', { oblik: e.value })} /></div>
                            <div className="zab-red"><span>Veličina</span><Slider value={cfg.slika.velicina} min={40} max={400} onChange={(e) => set('slika', { velicina: e.value })} className="zab-slider" /><span>{cfg.slika.velicina}px</span></div>
                            <div className="zab-red"><span>Brzina</span><Slider value={cfg.slika.brzina} min={1} max={10} onChange={(e) => set('slika', { brzina: e.value })} className="zab-slider" /><span>{cfg.slika.brzina}</span></div>
                            <div className="zab-red"><InputSwitch inputId="zab-sw-2" checked={!!cfg.slika.rotacija} onChange={(e) => set('slika', { rotacija: e.value })} /> <label htmlFor="zab-sw-2">Rotacija</label></div>
                        </Odeljak>

                        <Odeljak ikona="pi pi-volume-up" naslov="Zvuk u pozadini" opis="Efekat na svakih N sekundi ili radio. Kreće posle prvog klika korisnika (pravilo pregledača)."
                            ukljuceno={!!cfg.zvuk.ukljuceno} onUkljuceno={(v) => set('zvuk', { ukljuceno: v })}>
                            <SelectButton value={cfg.zvuk.vrsta} options={VRSTE_ZVUKA} onChange={(e) => e.value && set('zvuk', { vrsta: e.value })} />
                            {cfg.zvuk.vrsta === 'efekat' ? (
                                <>
                                    <div className="zab-red"><span>Efekat</span><Dropdown value={cfg.zvuk.efekat} options={NAZIVI_EFEKATA} onChange={(e) => set('zvuk', { efekat: e.value })} />
                                        <Button icon="pi pi-play" rounded text aria-label="Probaj efekat" tooltip="Probaj (kod mene)" onClick={() => pustiEfekat(cfg.zvuk.efekat, cfg.zvuk.jacina)} /></div>
                                    <div className="zab-red"><span>Na svakih</span><Slider value={cfg.zvuk.interval} min={5} max={300} onChange={(e) => set('zvuk', { interval: e.value })} className="zab-slider" /><span>{cfg.zvuk.interval}s</span></div>
                                </>
                            ) : (
                                <div className="zab-red"><span>Kanal</span><Dropdown value={cfg.zvuk.kanal} options={SRPSKI.map((k) => ({ label: k.title, value: k.stream }))}
                                    onChange={(e) => set('zvuk', { kanal: e.value })} placeholder="Izaberite radio" className="zab-puno" /></div>
                            )}
                            <div className="zab-red"><span>Jačina</span><Slider value={cfg.zvuk.jacina} min={0} max={70} onChange={(e) => set('zvuk', { jacina: e.value })} className="zab-slider" /><span>{cfg.zvuk.jacina}%</span></div>
                        </Odeljak>

                        <Odeljak ikona="pi pi-sparkles" naslov="Animacija preko ekrana" opis="Padaju preko ekrana; klikovi prolaze kroz njih."
                            ukljuceno={!!cfg.animacija.ukljuceno} onUkljuceno={(v) => set('animacija', { ukljuceno: v })}>
                            <SelectButton value={cfg.animacija.vrsta} options={ANIMACIJE} onChange={(e) => e.value && set('animacija', { vrsta: e.value })} />
                            {cfg.animacija.vrsta === 'emodzi' && (
                                <div className="zab-red"><span>Emodži</span><InputText value={cfg.animacija.emodzi} maxLength={8} onChange={(e) => set('animacija', { emodzi: e.target.value })} style={{ width: '6rem' }} />
                                    {['🎉', '🚚', '😂', '🔥', '❤️', '🍺', '⭐'].map((em) => <button key={em} type="button" className="zab-emodzi" onClick={() => set('animacija', { emodzi: em })}>{em}</button>)}</div>
                            )}
                            <div className="zab-red"><span>Gustina</span><Slider value={cfg.animacija.gustina} min={5} max={120} onChange={(e) => set('animacija', { gustina: e.value })} className="zab-slider" /><span>{cfg.animacija.gustina}</span></div>
                        </Odeljak>
                        <Odeljak ikona="pi pi-palette" naslov="Izgled aplikacije" opis="Tema, font i dugmad koja se klackaju — kod izabranih korisnika."
                            ukljuceno={(cfg.izgled || {}).tema !== 'normalna' || (cfg.izgled || {}).font === 'comic' || !!(cfg.izgled || {}).klackanje}
                            onUkljuceno={(v) => set('izgled', v ? { tema: 'duga' } : { tema: 'normalna', font: 'normalan', klackanje: false })}>
                            <div className="zab-teme">
                                {TEME.map((t) => (
                                    <button key={t.value} type="button" className={'zab-tema' + ((cfg.izgled || {}).tema === t.value ? ' active' : '')} onClick={() => set('izgled', { tema: t.value })}>
                                        <span className="zab-tema-boje">{t.boje.map((b) => <i key={b} style={{ background: b }} />)}</span>
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                            <div className="zab-red"><InputSwitch inputId="zab-sw-3" checked={(cfg.izgled || {}).font === 'comic'} onChange={(e) => set('izgled', { font: e.value ? 'comic' : 'normalan' })} /> <label htmlFor="zab-sw-3">Comic Sans font</label></div>
                            <div className="zab-red"><InputSwitch inputId="zab-sw-4" checked={!!(cfg.izgled || {}).klackanje} onChange={(e) => set('izgled', { klackanje: e.value })} /> <label htmlFor="zab-sw-4">Dugmad se klackaju na hover</label></div>
                        </Odeljak>

                        <Odeljak ikona="pi pi-arrow-up-left" naslov="Trag iza miša" opis="Emodži prate kursor dok se pomera."
                            ukljuceno={!!(cfg.kursor || {}).ukljuceno} onUkljuceno={(v) => set('kursor', { ukljuceno: v })}>
                            <div className="zab-red"><span>Emodži</span><InputText value={(cfg.kursor || {}).emodzi || ''} maxLength={8} onChange={(e) => set('kursor', { emodzi: e.target.value })} style={{ width: '6rem' }} />
                                {['✨', '⭐', '🚚', '💨', '🔥', '❤️', '🎈'].map((em) => <button key={em} type="button" className="zab-emodzi" onClick={() => set('kursor', { emodzi: em })}>{em}</button>)}</div>
                        </Odeljak>

                        <Odeljak ikona="pi pi-comment" naslov="Poruka usred ekrana" opis="Iskače kod korisnika dok ne klikne U redu; izmenjena poruka iskače ponovo."
                            ukljuceno={!!(cfg.poruka || {}).ukljuceno} onUkljuceno={(v) => set('poruka', { ukljuceno: v, verzija: Date.now() })}>
                            <InputText value={(cfg.poruka || {}).naslov || ''} maxLength={80} placeholder="Naslov" className="zab-puno"
                                onChange={(e) => set('poruka', { naslov: e.target.value, verzija: Date.now() })} />
                            <InputText value={(cfg.poruka || {}).tekst || ''} maxLength={600} placeholder="Tekst poruke" className="zab-puno"
                                onChange={(e) => set('poruka', { tekst: e.target.value, verzija: Date.now() })} />
                        </Odeljak>
                        <p className="zab-napomena"><i className="pi pi-shield" /> Granice: bez treptanja/bljeskanja, zvuk do 70%, slika i animacije ne blokiraju rad; uz sistemsko „smanji pokret“ animacije su blaže.</p>
                    </div>
                )}
            </Dialog>
        </>
    );
}
